import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatDate, isValidMobile } from "@/lib/format";
import { printSection } from "@/lib/print";
import { toast } from "@/components/ui/sonner";
import SettlementBox from "@/components/SettlementBox";
import SettlementVoucher from "@/components/SettlementVoucher";
import MobileInput from "@/components/MobileInput";
import { Users, Plus, X, Phone, MapPin, Car, Wallet, HandCoins } from "lucide-react";

const statusMeta = {
  available: { label: "In Stock", color: "#16A34A" },
  booked: { label: "Booked", color: "#D97706" },
  sold: { label: "Sold", color: "#2563EB" },
  delivered: { label: "Settled", color: "#475569" },
};

function dealsFor(m, vehicles) {
  const name = (m.name || "").trim().toLowerCase();
  return vehicles.filter(
    (v) => v.channel === "mediator" && (v.mediator_id === m.id || (v.party_name || "").trim().toLowerCase() === name)
  );
}

function heldOf(v) {
  return Math.max((v.total_buying_cost || 0) - (v.mediator_paid || 0), 0);
}

export default function Mediators() {
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ name: "", mobile: "", city: "" });
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null); // selected mediator
  const [openSettle, setOpenSettle] = useState(""); // deal id whose settle box is open
  const [settleVoucher, setSettleVoucher] = useState(null);

  const load = useCallback(async () => {
    const [m, v] = await Promise.all([api.get("/mediators"), api.get("/vehicles")]);
    setList(m.data);
    setVehicles(v.data);
    return v.data;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.mobile && !isValidMobile(form.mobile)) return toast.error("Please enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      await api.post("/mediators", form);
      toast.success("Mediator saved");
      setForm({ name: "", mobile: "", city: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const printSettlement = (data) => {
    setSettleVoucher(data);
    printSection("settlement");
  };

  const stat = (m) => {
    const d = dealsFor(m, vehicles);
    return {
      cars: d.length,
      brokerage: d.reduce((s, v) => s + (v.mediator_commission || 0), 0),
      held: d.reduce((s, v) => s + heldOf(v), 0),
    };
  };

  const activeDeals = active ? dealsFor(active, vehicles) : [];
  const activeStat = active ? stat(active) : null;

  return (
    <div className="a1-fade max-w-5xl">
      <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight mb-1">Regular Mediators</h1>
      <p className="text-slate-500 font-semibold mb-6">Tap a mediator to view their profile, deal history &amp; held balances.</p>

      <form onSubmit={add} className="a1-card p-6 mb-6 grid md:grid-cols-4 gap-4 items-end">
        <div className="md:col-span-1">
          <label className="a1-label">Name</label>
          <input data-testid="mediator-name" className="a1-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="a1-label">Mobile</label>
          <MobileInput testid="mediator-mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} />
        </div>
        <div>
          <label className="a1-label">City</label>
          <input data-testid="mediator-city" className="a1-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </div>
        <button data-testid="mediator-add" disabled={busy} className="a1-btn a1-btn-action">
          <Plus className="w-5 h-5" /> Add
        </button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <p className="text-slate-400 font-semibold">No mediators saved yet.</p>}
        {list.map((m) => {
          const s = stat(m);
          return (
            <button
              key={m.id}
              onClick={() => setActive(m)}
              className="a1-card p-5 text-left hover:border-slate-300 transition-colors"
              data-testid={`mediator-card-${m.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-head font-extrabold text-lg tracking-tight">{m.name}</span>
              </div>
              <div className="text-sm font-semibold text-slate-500">{m.mobile || "No mobile"}</div>
              <div className="text-sm font-semibold text-slate-400 mb-3">{m.city || "—"}</div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{s.cars} car{s.cars === 1 ? "" : "s"}</span>
                {s.held > 0 && (
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: "#FFF7ED", color: "#EA580C" }}>
                    {formatINR(s.held)} held
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Settlement voucher (rendered at page root, outside .no-print) */}
      <SettlementVoucher data={settleVoucher} />

      {/* Profile Modal — full-width responsive */}
      {active && (
        <div className="no-print" style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 12px", overflowY: "auto" }}>
          <div onClick={() => { setActive(null); setOpenSettle(""); }} style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.5)" }} />
          <div
            className="a1-fade"
            style={{ position: "relative", width: "100%", maxWidth: 1000, background: "#fff", borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,.3)", overflow: "hidden" }}
            data-testid="mediator-drawer"
          >
            {/* Header */}
            <div className="sticky top-0 bg-[#1E3A8A] text-white px-6 py-5 flex items-start justify-between">
              <div>
                <div className="font-head font-black text-2xl tracking-tight">{active.name}</div>
                <div className="flex gap-4 mt-1 text-sm font-semibold text-blue-100">
                  <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {active.mobile || "—"}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {active.city || "—"}</span>
                </div>
              </div>
              <button data-testid="drawer-close" onClick={() => { setActive(null); setOpenSettle(""); }} className="text-white/80 hover:text-white">
                <X className="w-7 h-7" />
              </button>
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-3 gap-3 p-6">
              <div className="a1-card p-4 text-center">
                <Car className="w-6 h-6 mx-auto mb-1 text-[#1E3A8A]" />
                <div className="num font-head font-black text-2xl">{activeStat.cars}</div>
                <div className="text-xs font-bold text-slate-500">Cars Sourced</div>
              </div>
              <div className="a1-card p-4 text-center">
                <HandCoins className="w-6 h-6 mx-auto mb-1 text-[#2563EB]" />
                <div className="num font-head font-black text-xl">{formatINR(activeStat.brokerage)}</div>
                <div className="text-xs font-bold text-slate-500">Brokerage Earned</div>
              </div>
              <div className="a1-card p-4 text-center">
                <Wallet className="w-6 h-6 mx-auto mb-1 text-[#EA580C]" />
                <div className="num font-head font-black text-xl" style={{ color: activeStat.held > 0 ? "#EA580C" : "#16A34A" }}>{formatINR(activeStat.held)}</div>
                <div className="text-xs font-bold text-slate-500">Pending Held Owed</div>
              </div>
            </div>

            {/* Deal history */}
            <div className="px-6 pb-8">
              <h3 className="font-head font-extrabold text-xl tracking-tight mb-3">Deal History</h3>
              {activeDeals.length === 0 && <p className="text-slate-400 font-semibold">No cars sourced by this mediator yet.</p>}
              <div className="space-y-3">
                {activeDeals.map((v) => {
                  const held = heldOf(v);
                  const sm = held <= 0 && (v.status === "sold" || v.status === "delivered")
                    ? { label: "Settled", color: "#475569" }
                    : statusMeta[v.status] || statusMeta.available;
                  return (
                    <div key={v.id} className="a1-card p-4" data-testid={`deal-${v.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-head font-bold tracking-tight">{v.model}</div>
                          <div className="num text-sm font-semibold text-slate-500">{v.reg_no} • {formatDate(v.inward_date)}</div>
                        </div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: sm.color }}>{sm.label}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <div><div className="text-xs font-bold text-slate-400">Purchase Rate</div><div className="num font-bold">{formatINR(v.total_buying_cost || v.office_purchase_rate)}</div></div>
                        <div><div className="text-xs font-bold text-slate-400">Brokerage</div><div className="num font-bold text-[#2563EB]">{formatINR(v.mediator_commission || 0)}</div></div>
                        <div><div className="text-xs font-bold text-slate-400">Upfront Paid</div><div className="num font-bold text-[#16A34A]">{formatINR(v.mediator_paid || 0)}</div></div>
                        <div><div className="text-xs font-bold text-slate-400">Held Balance</div><div className="num font-bold" style={{ color: held > 0 ? "#EA580C" : "#16A34A" }}>{formatINR(held)}</div></div>
                      </div>
                      {held > 0 && (
                        <div className="mt-3">
                          {openSettle === v.id ? (
                            <SettlementBox
                              vehicle={v}
                              hold={held}
                              compact
                              onSettled={async () => { await load(); setOpenSettle(""); }}
                              onPrint={printSettlement}
                            />
                          ) : (
                            <button
                              data-testid={`settle-${v.id}`}
                              onClick={() => setOpenSettle(v.id)}
                              className="a1-btn a1-btn-action w-full !h-11 !text-sm"
                            >
                              Settle Balance • {formatINR(held)}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
